import { MAX_CLIENT_REPRESENTATIVES } from "@repo/shared/types";
import { Button } from "@/components/ui/button";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/lib/i18n/use-t";
import type { ComponentStyleType } from "@/lib/types/flow";
import { T } from "./trapets-lookup-form.i18n";
import type { IdMode, RepresentativeFormState } from "./trapets-lookup-widget";
import {
  COUNTRY_OPTIONS,
  IdentifierFields,
  RepresentativeFields,
} from "./trapets-representative-fields";

type TrapetsLookupFormProps = {
  isCompany: boolean;
  /** A Group has no identity of its own to screen — no entity search at all. */
  isGroup: boolean;
  /** QL-21 follow-up / QL-654 — the natural persons screened on a Company/Group's behalf. */
  reps: RepresentativeFormState[];
  updateRep: (index: number, patch: Partial<RepresentativeFormState>) => void;
  addRep: () => void;
  removeRep: (index: number) => void;
  /** Which representative sections failed validation, keyed by the form row's own key. */
  repErrors: Record<string, boolean>;
  name: string;
  setName: (value: string) => void;
  country: string;
  setCountry: (value: string) => void;
  ssn: string;
  setSSN: (value: string) => void;
  /** QL-c — national id OR date of birth; only the selected one is searched. */
  idMode: IdMode;
  setIdMode: (value: IdMode) => void;
  birthDate: string;
  setBirthDate: (value: string) => void;
  orgId: string;
  setOrgId: (value: string) => void;
  onSearch: () => void;
  isPending: boolean;
  disabled: boolean;
  error: string | null;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  hasValidationError?: boolean;
};

export function TrapetsLookupForm({
  isCompany,
  isGroup,
  reps,
  updateRep,
  addRep,
  removeRep,
  repErrors,
  name,
  setName,
  country,
  setCountry,
  ssn,
  setSSN,
  idMode,
  setIdMode,
  birthDate,
  setBirthDate,
  orgId,
  setOrgId,
  onSearch,
  isPending,
  disabled,
  error,
  style = "none",
  title,
  description,
  hasValidationError = false,
}: TrapetsLookupFormProps) {
  const t = useT(T);
  const getContainerClass = () => {
    if (hasValidationError) {
      return "border border-destructive rounded-lg p-6 space-y-4";
    }
    switch (style) {
      case "border":
        return "border rounded-lg p-6 space-y-4";
      case "box":
        return "rounded-lg p-6 space-y-4 bg-muted/50";
      default:
        return "space-y-4";
    }
  };

  return (
    <div className={getContainerClass()}>
      <FlowComponentHeader title={title} description={description} />
      {hasValidationError && <p className="text-sm text-destructive">{t.incompleteMessage}</p>}

      <div className="space-y-4">
        {error && (
          <div className="p-3 rounded-md text-sm bg-destructive/10 text-destructive border border-destructive/30">
            {error}
          </div>
        )}

        {/* QL-21 follow-up — a Company screening is TWO searches on TWO parties,
            and which party answers which question is not guessable. The form
            says it explicitly rather than leaving the advisor to infer it from
            a single undifferentiated set of fields. */}
        {isCompany && (
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-medium">
              {isGroup ? t.groupSection : t.companySection}
            </span>
            <span className="text-xs text-muted-foreground">
              {isGroup ? t.groupChecked : t.companyChecked}
            </span>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="trapets-name">
              {isCompany ? (isGroup ? t.groupName : t.companyName) : t.name}{" "}
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="trapets-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.fullName}
              disabled={isPending || disabled}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="trapets-country">
              {t.country} <span className="text-destructive">*</span>
            </Label>
            <Select value={country} onValueChange={setCountry} disabled={isPending || disabled}>
              <SelectTrigger id="trapets-country">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.code} value={opt.code}>
                    {opt.name} ({opt.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isCompany && !isGroup && (
          <div className="space-y-2">
            <Label htmlFor="trapets-orgid">{t.organizationNumber}</Label>
            <Input
              id="trapets-orgid"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              placeholder={t.orgIdPlaceholder}
              disabled={isPending || disabled}
            />
            <p className="text-xs text-muted-foreground">{t.optionalImproves}</p>
          </div>
        )}

        {isCompany ? (
          // The representative blocks. PEP is a property of a natural person, so
          // these are the parties the PEP question is actually put to — and the
          // sanction check covers them as well as the company. QL-654: one block
          // per representative; every one is screened.
          <>
            {reps.map((rep, index) => (
              <RepresentativeFields
                key={rep.key}
                rep={rep}
                index={index}
                total={reps.length}
                onChange={(patch) => updateRep(index, patch)}
                onRemove={() => removeRep(index)}
                error={repErrors[rep.key] ?? false}
                disabled={isPending || disabled}
              />
            ))}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRep}
                disabled={
                  isPending ||
                  disabled ||
                  reps.length >= MAX_CLIENT_REPRESENTATIVES ||
                  // Once the client has registered representatives, screening
                  // never adds another: the server refuses an unregistered person.
                  reps.some((r) => r.partyId !== null)
                }
              >
                {t.addRepresentative}
              </Button>
              <p className="text-xs text-muted-foreground">
                {reps.length >= MAX_CLIENT_REPRESENTATIVES
                  ? t.representativeLimit(MAX_CLIENT_REPRESENTATIVES)
                  : t.representativeSaved}
              </p>
            </div>
          </>
        ) : (
          // QL-c — a Person is screened by national id OR date of birth.
          //
          // QL-21 removed the date-of-birth option because the compliance batch
          // lookup required an SSN, so a flow could screen someone Compliance
          // could never re-screen. That gap is closed: the DOB now lives on the
          // client and `resolveScreeningPlan` accepts either, so both paths
          // agree again. The lists themselves hold no national ids — they record
          // name + birth date + country — so this is a complete search, and the
          // only one available in markets without a national id number.
          <div className="grid gap-4 md:grid-cols-2">
            <IdentifierFields
              idPrefix="trapets"
              mode={idMode}
              setMode={setIdMode}
              ssn={ssn}
              setSSN={setSSN}
              birthDate={birthDate}
              setBirthDate={setBirthDate}
              country={country}
              disabled={isPending || disabled}
            />
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={onSearch} disabled={disabled} loading={isPending}>
            {t.runSearch}
          </Button>
        </div>
      </div>
    </div>
  );
}
